import { ref, computed } from 'vue';
import { apolloService } from '@/apollo/apolloRequests.ts';
import { GET_MERIT_ITEMS_FROM_ENTY } from './queries';
import {
  CREATE_INVOICE_IN_MERIT_BY_FILE_ID,
  DELETE_MERIT_INVOICE,
  DELETE_MERIT_PURCHASE_INVOICE,
  DELETE_MERIT_PAYMENT,
  SEND_CHARGE_BE_INVOICE_TO_MERIT,
  SEND_EXPENSE_PAYMENT_TO_MERIT,
  SEND_INCOME_PAYMENT_TO_MERIT,
  SEND_PURCHASE_INVOICE_TO_MERIT,
  WRITE_OFF_MERIT_FX_DIFFERENCE,
} from './mutations';
import { MeritItemEntity, InvoicingMutation, InvoicingQuery, AccountingMutation, SendChargebeeInvoiceToMeritResultType, ItemTypeEnum } from '@/generated/graphql';
import { Dialog, Notify } from 'quasar';
import VatErrorConfirmationModal from './VatErrorConfirmationModal.vue';
import ChargebeeInvoiceErrorConfirmModal from './ChargebeeInvoiceErrorConfirmModal.vue';

export interface IMeritSendResult {
  success: boolean;
  errorMessage?: string;
  // The send was stopped by the user in a confirmation dialog, not by an error
  cancelled?: boolean;
}

export interface IMeritSendOptions {
  // Callers that send several files build their own summary,
  // so per-file notifications are suppressed
  silent?: boolean;
  // Shown in confirmation dialogs so it is clear which file they are about
  fileName?: string;
}

export function useMeritIntegration() {
  const meritItemsFromEnty = ref<MeritItemEntity[]>([]);
  const isLoadingMeritItems = ref(false);
  const BAD_REQUEST_EXCEPTION_PREFIX = 'BadRequestException:';

  // Computed property to check if all categories have meritItemCode
  const allCategoriesHaveMeritItemCode = computed(() => {
    return meritItemsFromEnty.value.length > 0 &&
      meritItemsFromEnty.value.every(item => !!item.meritItemCode);
  });

  /**
   * Checks if Merit integration is properly configured
   * @returns A boolean indicating if Merit integration is properly configured
   */
  const canSendToMerit = (): boolean => {
    // Merit integration is properly configured when all categories have meritItemCode
    return allCategoriesHaveMeritItemCode.value;
  };

  /**
   * Fetches Merit items from Enty for a specific company
   * @param companyId - The ID of the company
   * @returns A promise that resolves to an array of MeritItemEntity objects
   */
  const fetchMeritItemsFromEnty = async (companyId: string): Promise<MeritItemEntity[]> => {
    if (!companyId) {
      return [];
    }
    isLoadingMeritItems.value = true;
    try {
      const entyData = await apolloService.makeApolloRequest(
        'query',
        GET_MERIT_ITEMS_FROM_ENTY,
        { companyId, type: ItemTypeEnum.Sales },
      );

      const items = (entyData?.invoicing as InvoicingQuery)?.getMeritItemsFromEnty || [];
      meritItemsFromEnty.value = items;

      return items;
    } catch (error) {
      console.error('Error fetching Merit items from Enty:', error);
      return [];
    } finally {
      isLoadingMeritItems.value = false;
    }
  };

  /**
   * Sends an invoice to Merit
   * @param companyId - The ID of the company
   * @param aFileId - The ID of the doc to send to Merit
   * @param skipChecks - Flag to skip validation checks (defaults to false)
   * @param customVatNumber - Optional custom VAT number to use (only sent when skipChecks is true)
   * @returns A promise that resolves when the invoice is sent to Merit
   */
  /**
   * Handles errors that occur when sending an invoice to Merit
   * @param error - The error that occurred
   * @returns An object with a handled status and error message
   */
  const handleMeritInvoiceError = (error: unknown): { handled: boolean; errorMessage: string } => {
    // Check for specific VAT-related errors
    let errorMessage = error instanceof Error ? error.message : String(error);

    // Remove BAD_REQUEST_EXCEPTION_PREFIX if present
    if (errorMessage.startsWith(BAD_REQUEST_EXCEPTION_PREFIX)) {
      errorMessage = errorMessage.replace(BAD_REQUEST_EXCEPTION_PREFIX, '').trim();
    }

    // Define patterns for different types of VAT errors
    const vatNumberInvalidPattern = /vat.*invalid|invalid.*vat/i;
    const companyNameMismatchPattern = /vat.*belong.*company|company.*belong.*vat/i;
    const countryMismatchPattern = /country.*vat.*country|vat.*country.*match/i;

    // Check if the error message matches any of the patterns
    const isVatNumberInvalid = vatNumberInvalidPattern.test(errorMessage);
    const isCompanyNameMismatch = companyNameMismatchPattern.test(errorMessage);
    const isCountryMismatch = countryMismatchPattern.test(errorMessage);

    const hasVatError = isVatNumberInvalid || isCompanyNameMismatch ||
                        isCountryMismatch;

    if (hasVatError) {
      return { handled: true, errorMessage };
    } else {
      return { handled: false, errorMessage };
    }
  };

  const sendInvoiceToMeritWithResult = async (
    companyId: string,
    aFileId: string,
    options: IMeritSendOptions & { customVatNumber?: string; skipChecks?: boolean } = {},
  ): Promise<IMeritSendResult> => {
    const { customVatNumber, skipChecks = false, silent = false, fileName } = options;
    try {
      const params = skipChecks
        ? { companyId, aFileId, skipChecks, customVatNumber }
        : { companyId, aFileId, skipChecks };

      const result = await apolloService.makeApolloRequest(
        'mutation',
        CREATE_INVOICE_IN_MERIT_BY_FILE_ID,
        params,
        undefined,
        false,
      );

      if ((result?.invoicing as InvoicingMutation)?.createInvoiceInMeritByAFileId) {
        return { success: true };
      }

      return { success: false, errorMessage: 'Failed to send the invoice to Merit' };
    } catch (error) {
      const { handled, errorMessage } = handleMeritInvoiceError(error);
      if (!silent) {
        Notify.create({
          message: errorMessage,
          color: 'negative',
          position: 'top',
        });
      }

      if (!handled) {
        return { success: false, errorMessage };
      }

      return new Promise((resolve) => {
        Dialog.create({
          component: VatErrorConfirmationModal,
          componentProps: {
            companyId,
            aFileId,
            customerVatNumber: customVatNumber,
            errorMessage,
            fileName,
          },
        }).onOk(async (data) => {
          resolve(await sendInvoiceToMeritWithResult(data.companyId, data.aFileId, {
            ...options,
            customVatNumber: data.customVatNumber,
            skipChecks: data.skipChecks,
          }));
        }).onCancel(() => {
          resolve({ success: false, cancelled: true, errorMessage });
        });
      });
    }
  };

  const sendInvoiceToMerit = async (
    companyId: string,
    aFileId: string,
    customVatNumber?: string,
    skipChecks: boolean = false,
  ): Promise<boolean> => {
    const { success } = await sendInvoiceToMeritWithResult(companyId, aFileId, { customVatNumber, skipChecks });
    return success;
  };

  const sendIncomePaymentToMerit = async (transactionId: string): Promise<boolean> => {
    try {
      const result = await apolloService.makeApolloRequest(
        'mutation',
        SEND_INCOME_PAYMENT_TO_MERIT,
        { transactionId },
        undefined,
        false,
      );

      const payload = (result?.accounting as { sendIncomePaymentToMerit?: { success?: boolean | null; errorMessage?: string | null } })?.sendIncomePaymentToMerit;

      if (payload?.success) {
        Notify.create({
          message: 'Income payment has been successfully sent to Merit',
          color: 'positive',
          position: 'top',
        });
        return true;
      }

      Notify.create({
        message: payload?.errorMessage || 'Failed to send income payment to Merit',
        color: 'negative',
        position: 'top',
      });
      return false;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to send income payment to Merit';
      Notify.create({
        message,
        color: 'negative',
        position: 'top',
      });
      return false;
    }
  };

  const sendExpensePaymentToMerit = async (transactionId: string): Promise<boolean> => {
    try {
      const result = await apolloService.makeApolloRequest(
        'mutation',
        SEND_EXPENSE_PAYMENT_TO_MERIT,
        { transactionId },
        undefined,
        false,
      );

      const payload = (result?.accounting as { sendExpensePaymentToMerit?: { success?: boolean | null; errorMessage?: string | null } })?.sendExpensePaymentToMerit;

      if (payload?.success) {
        Notify.create({
          message: 'Expense payment has been successfully sent to Merit',
          color: 'positive',
          position: 'top',
        });
        return true;
      }

      Notify.create({
        message: payload?.errorMessage || 'Failed to send expense payment to Merit',
        color: 'negative',
        position: 'top',
      });
      return false;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to send expense payment to Merit';
      Notify.create({
        message,
        color: 'negative',
        position: 'top',
      });
      return false;
    }
  };

  const sendPurchaseInvoiceToMeritWithResult = async (
    accountingFileId: string,
    options: IMeritSendOptions & { ignoreScore?: boolean } = {},
  ): Promise<IMeritSendResult> => {
    const { ignoreScore = false, silent = false, fileName } = options;
    try {
      const result = await apolloService.makeApolloRequest(
        'mutation',
        SEND_PURCHASE_INVOICE_TO_MERIT,
        { accountingFileId, ignoreScore },
        undefined,
        false,
      );

      const payload = ((result?.accounting as AccountingMutation)?.sendPurchaseInvoiceToMerit) as (SendChargebeeInvoiceToMeritResultType & {
        isValidationError?: boolean | null;
        validationErrors?: string[] | null;
      });

      if (payload?.success) {
        if (!silent) {
          Notify.create({
            message: 'Invoice has been successfully sent to Merit',
            color: 'positive',
            position: 'top',
          });
        }
        return { success: true };
      }

      if (payload?.isValidationError && !ignoreScore) {
        return new Promise((resolve) => {
          Dialog.create({
            component: ChargebeeInvoiceErrorConfirmModal,
            componentProps: {
              aFileId: accountingFileId,
              validationErrors: payload?.validationErrors || [],
              title: 'Still send the invoice?',
              errorsLabel: 'The invoice is missing some data:',
              fileName,
            },
          })
            .onOk(async (data) => {
              resolve(await sendPurchaseInvoiceToMeritWithResult(data.aFileId, { ...options, ignoreScore: true }));
            })
            .onCancel(() => {
              resolve({ success: false, cancelled: true });
            });
        });
      }

      const errorMessage = payload?.errorMessage || 'Failed to send the invoice to Merit';
      if (!silent) {
        Notify.create({
          message: errorMessage,
          color: 'negative',
          position: 'top',
        });
      }
      return { success: false, errorMessage };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send the invoice to Merit';
      if (!silent) {
        Notify.create({
          message: errorMessage,
          color: 'negative',
          position: 'top',
        });
      }
      return { success: false, errorMessage };
    }
  };

  const sendPurchaseInvoiceToMerit = async (
    accountingFileId: string,
    ignoreScore: boolean = false,
  ): Promise<boolean> => {
    const { success } = await sendPurchaseInvoiceToMeritWithResult(accountingFileId, { ignoreScore });
    return success;
  };

  const sendChargebeeInvoiceToMeritWithResult = async (
    aFileId: string,
    options: IMeritSendOptions & { forceSubmit?: boolean } = {},
  ): Promise<IMeritSendResult> => {
    const { forceSubmit = false, silent = false, fileName } = options;
    try {
      const result = await apolloService.makeApolloRequest(
        'mutation',
        SEND_CHARGE_BE_INVOICE_TO_MERIT,
        {
          chargebeeInvoiceId: aFileId,
          forceSubmit,
        },
        undefined,
        false,
      );

      const payload = ((result?.accounting as AccountingMutation)?.sendChargebeeInvoiceToMerit) as (SendChargebeeInvoiceToMeritResultType & {
        isValidationError?: boolean | null;
        validationErrors?: string[] | null;
      });

      if (payload?.success) {
        if (!silent) {
          Notify.create({
            message: 'Invoice has been successfully sent to Merit',
            color: 'positive',
            position: 'top',
          });
        }
        return { success: true };
      }

      if (payload?.isValidationError) {
        return new Promise((resolve) => {
          Dialog.create({
            component: ChargebeeInvoiceErrorConfirmModal,
            componentProps: {
              aFileId,
              validationErrors: payload?.validationErrors || [],
              fileName,
            },
          })
            .onOk(async (data) => {
              resolve(await sendChargebeeInvoiceToMeritWithResult(data.aFileId, { ...options, forceSubmit: true }));
            })
            .onCancel(() => {
              resolve({ success: false, cancelled: true });
            });
        });
      }

      const errorMessage = payload?.errorMessage || 'Failed to send the invoice to Merit';
      if (!silent) {
        Notify.create({
          message: errorMessage,
          color: 'negative',
          position: 'top',
        });
      }
      return { success: false, errorMessage };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send the invoice to Merit';
      if (!silent) {
        Notify.create({
          message: errorMessage,
          color: 'negative',
          position: 'top',
        });
      }
      return { success: false, errorMessage };
    }
  };

  const sendChargebeeInvoiceToMerit = async (
    aFileId: string,
    forceSubmit: boolean = false,
  ): Promise<boolean> => {
    const { success } = await sendChargebeeInvoiceToMeritWithResult(aFileId, { forceSubmit });
    return success;
  };

  const deleteMeritInvoice = async (fileId: string): Promise<boolean> => {
    try {
      await apolloService.makeApolloRequest(
        'mutation',
        DELETE_MERIT_INVOICE,
        { fileId },
        undefined,
        false,
      );
      Notify.create({
        message: 'Invoice deleted from Merit',
        color: 'positive',
        position: 'top',
      });
      return true;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete invoice from Merit';
      Notify.create({
        message,
        color: 'negative',
        position: 'top',
      });
      return false;
    }
  };

  const deleteMeritPurchaseInvoice = async (accountingFileId: string): Promise<boolean> => {
    try {
      const result = await apolloService.makeApolloRequest(
        'mutation',
        DELETE_MERIT_PURCHASE_INVOICE,
        { accountingFileId },
        undefined,
        false,
      );
      const ok = (result?.accounting as { deleteMeritPurchaseInvoice?: boolean | null })
        ?.deleteMeritPurchaseInvoice;
      if (ok) {
        Notify.create({
          message: 'Invoice deleted from Merit',
          color: 'positive',
          position: 'top',
        });
        return true;
      }
      Notify.create({
        message: 'Failed to delete invoice from Merit',
        color: 'negative',
        position: 'top',
      });
      return false;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to delete invoice from Merit';
      Notify.create({
        message,
        color: 'negative',
        position: 'top',
      });
      return false;
    }
  };

  const deleteMeritPayment = async (transactionId: string): Promise<boolean> => {
    try {
      const result = await apolloService.makeApolloRequest(
        'mutation',
        DELETE_MERIT_PAYMENT,
        { transactionId },
        undefined,
        false,
      );
      const ok = (result?.accounting as { deleteMeritPayment?: boolean | null })?.deleteMeritPayment;
      if (ok) {
        Notify.create({
          message: 'Payment deleted from Merit',
          color: 'positive',
          position: 'top',
        });
        return true;
      }
      Notify.create({
        message: 'Failed to delete payment from Merit',
        color: 'negative',
        position: 'top',
      });
      return false;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to delete payment from Merit';
      Notify.create({
        message,
        color: 'negative',
        position: 'top',
      });
      return false;
    }
  };

  const writeOffMeritFxDifference = async (
    companyId: string,
    year: number,
    month: number,
    amount: number,
  ): Promise<boolean> => {
    try {
      const result = await apolloService.makeApolloRequest(
        'mutation',
        WRITE_OFF_MERIT_FX_DIFFERENCE,
        { companyId, year, month, amount },
        undefined,
        false,
      );
      const ok = (result?.accounting as { writeOffMeritFxDifference?: boolean | null })?.writeOffMeritFxDifference;
      if (ok) {
        Notify.create({
          message: 'FX difference has been written off in Merit',
          color: 'positive',
          position: 'top',
        });
        return true;
      }
      Notify.create({
        message: 'Failed to write off FX difference in Merit',
        color: 'negative',
        position: 'top',
      });
      return false;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to write off FX difference in Merit';
      Notify.create({
        message,
        color: 'negative',
        position: 'top',
      });
      return false;
    }
  };

  return {
    meritItemsFromEnty,
    isLoadingMeritItems,
    allCategoriesHaveMeritItemCode,
    fetchMeritItemsFromEnty,
    canSendToMerit,
    sendInvoiceToMerit,
    sendInvoiceToMeritWithResult,
    sendIncomePaymentToMerit,
    sendExpensePaymentToMerit,
    sendPurchaseInvoiceToMerit,
    sendPurchaseInvoiceToMeritWithResult,
    sendChargebeeInvoiceToMerit,
    sendChargebeeInvoiceToMeritWithResult,
    deleteMeritInvoice,
    deleteMeritPurchaseInvoice,
    deleteMeritPayment,
    writeOffMeritFxDifference,
  };
}
